import { ChevronDownIcon, PlusIcon } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ChangeBadge } from '@/components/atoms/change-badge'
import { Button } from '@/components/ui/button'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { cn } from '@/lib/utils'
import { MetChange } from '@/utils/service-diff.utils'

import { ServiceGroup } from '../../../../../../backend/payload/payload-types'
import { ServiceGroupGeneration } from '../../../../../../backend/schemas/src/service-groups'
import ServiceRecommendation, { ServiceRecommendationPill } from './service-recommendation'

type Props = {
  change?: MetChange
  generation?: NonNullable<ServiceGroupGeneration['services']>[number]
  // Opens the create-errand dialog preselected to this service. Owned by the page so a
  // single dialog instance is shared across every card instead of one per card.
  onStartErrand(serviceId: string): void
  // Only the fields the card actually renders — accepts both the Payload service type and the
  // (leaner) API response service type, which omits fields like `active`.
  service: Pick<ServiceGroup['services'][number], 'id' | 'title' | 'description'>
  showFullDescription?: boolean
}

const ServiceCard = ({
  change,
  generation,
  onStartErrand,
  service,
  showFullDescription = false,
}: Props) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(showFullDescription)

  return (
    <Collapsible onOpenChange={setOpen} open={open}>
      <div className="card rounded-md gap-0 p-0">
        <CollapsibleTrigger className="flex flex-wrap items-center justify-between w-full gap-x-4 gap-y-2 text-left group hover:bg-layer-2 p-3 px-4 data-[state=open]:bg-layer-2 data-[state=open]:hover:bg-layer-2 sm:flex-nowrap">
          {/* Full width on mobile so the badge/pill/chevron drop to their own line
              instead of squeezing the title into a narrow column. */}
          <h4 className="font-bold text-lg flex-1 basis-full sm:basis-auto">{service.title}</h4>
          <ChangeBadge change={change} />
          {generation?.met && <ServiceRecommendationPill generation={generation} />}
          <ChevronDownIcon
            className={cn('size-4 shrink-0 text-text-secondary transition-transform duration-200', {
              'rotate-180': open,
            })}
          />
        </CollapsibleTrigger>
        <CollapsibleContent className="overflow-hidden px-5">
          <div className="flex flex-col gap-4 py-4">
            {service.description && (
              <p className="max-w-prose whitespace-pre-wrap">{service.description}</p>
            )}
            {!!generation?.explanation && (
              <div>
                <ServiceRecommendation generation={generation} showPill={false} />
              </div>
            )}
            <div className="flex justify-start">
              <Button onClick={() => onStartErrand(service.id)} type="button" variant="outline">
                <PlusIcon />
                {t('errandsPage.createErrandButton')}
              </Button>
            </div>
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  )
}

export default ServiceCard
